import type { ConsentTemplate, ConsentTemplateInput } from "../../../api/models/consentModels";
import {sortConsentInputsByDocument} from "@source/api/models/consentHelpers";
import type { AutomaticKey, BlockStyle, InputActor, InputBinding, InputDefinitions, ParagraphSegment, PuckBlock, TemplateComponents, TemplateData, TemplatePayload } from "./types";
import { automaticKeyValues, findInputDefinition, isParagraphVariable, paragraphVariables } from "./variables";

const inputBlockTypes = new Set(["TextInput", "TextArea", "DateInput", "DateTimeInput", "Checkbox", "CheckboxGroup", "RadioInput"]);
const validFontFamilies = new Set(["Courier", "Helvetica", "Times-Roman", "Symbol", "ZapfDingbats"]);
const validBlockTypes = new Set<keyof TemplateComponents>([
  "Page", "DocumentTitle", "AutomaticValue", "InlineAutomaticValue", "SectionHeading",
  "Paragraph", "Link", "TextInput", "TextArea", "DateInput", "DateTimeInput", "Checkbox", "CheckboxGroup",
  "RadioInput", "Divider", "Table",
]);
const legacyVariableAliases: Record<string, AutomaticKey> = {
  residentName: "resident.name",
  residentRoom: "resident.room",
  employeeName: "employee.name",
  today: "document.date",
};
const automaticField = (value: unknown): AutomaticKey => {
  const field = typeof value === "string" ? legacyVariableAliases[value] ?? value : undefined;
  return automaticKeyValues.includes(field as AutomaticKey) ? field as AutomaticKey : "resident.name";
};

const parsePersistedObject = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value === "string") {
    try {
      return parsePersistedObject(JSON.parse(value));
    } catch {
      return undefined;
    }
  }
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
};

const parsePersistedArray = (value: unknown): unknown[] | undefined => {
  if (typeof value === "string") {
    try {
      return parsePersistedArray(JSON.parse(value));
    } catch {
      return undefined;
    }
  }
  return Array.isArray(value) ? value : undefined;
};

const pageOnlyRoot = (blocks: PuckBlock[], createPageId?: () => string): PuckBlock[] => {
  // Versions that predate the root allow-list can have document blocks at the
  // root. Keep each contiguous run together in a new page instead of
  // discarding user content or changing the order of existing pages.
  const result: PuckBlock[] = [];
  let legacyRun: PuckBlock[] = [];
  const flushLegacyRun = () => {
    if (legacyRun.length) result.push({ type: "Page", props: { ...(createPageId ? { id: createPageId() } : {}), content: legacyRun } });
    legacyRun = [];
  };
  blocks.forEach((block) => {
    if (block.type === "Page") { flushLegacyRun(); result.push(block); }
    else legacyRun.push(block);
  });
  flushLegacyRun();
  return result;
};

export const normalizeParagraphSegments = (value: unknown, fallback = ""): ParagraphSegment[] => {
  const source = Array.isArray(value) && value.length ? value : [{ text: fallback }];
  const result: ParagraphSegment[] = [];
  source.forEach((segment) => {
    if (!segment || typeof segment !== "object" || Array.isArray(segment)) return;
    if ("text" in segment && typeof segment.text === "string") {
      if (!segment.text) return;
      const previous = result[result.length - 1];
      if (previous && "text" in previous) previous.text += segment.text;
      else result.push({ text: segment.text });
    } else if ("variable" in segment && typeof segment.variable === "string" && segment.variable) {
      const variable = legacyVariableAliases[segment.variable] ?? segment.variable;
      if (isParagraphVariable(variable)) result.push({ variable });
    }
  });
  return result.length ? result : [{ text: "" }];
};

const TEMPLATE_PLACEHOLDER = /{{\s*([a-zA-Z][a-zA-Z0-9_]*(?:\.[a-zA-Z][a-zA-Z0-9_-]*){1,2})\s*}}/g;

export const parseTemplateText = (value: unknown): ParagraphSegment[] => {
  if (Array.isArray(value)) return normalizeParagraphSegments(value);
  const text = typeof value === "string" ? value : "";
  const parts: unknown[] = [];
  let last = 0;
  TEMPLATE_PLACEHOLDER.lastIndex = 0;
  for (let match = TEMPLATE_PLACEHOLDER.exec(text); match; match = TEMPLATE_PLACEHOLDER.exec(text)) {
    const index = match.index;
    if (index > last) parts.push({ text: text.slice(last, index) });
    const variable = legacyVariableAliases[match[1]] ?? match[1];
    parts.push(isParagraphVariable(variable) ? { variable } : { text: match[0] });
    last = index + match[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return normalizeParagraphSegments(parts, text);
};

export const serializeTemplateText = (segments: ParagraphSegment[]) =>
  normalizeParagraphSegments(segments).map((part) => "text" in part ? part.text : `{{${part.variable}}}`).join("");

export const normalizeStyle = (value: unknown): BlockStyle | undefined => {
  // The editor stores style as JSON. A new style key must not become a nested
  // Puck prop: the next slot walk treats that new key as a missing block.
  if (typeof value === "string") {
    try { value = JSON.parse(value); } catch { return undefined; }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const source = value as Record<string, unknown>;
  const style: BlockStyle = {};
  if (typeof source.fontFamily === "string" && validFontFamilies.has(source.fontFamily)) style.fontFamily = source.fontFamily as BlockStyle["fontFamily"];
  if (typeof source.fontSize === "number" && source.fontSize >= 6 && source.fontSize <= 72) style.fontSize = source.fontSize;
  if (source.fontWeight === "normal" || source.fontWeight === "bold") style.fontWeight = source.fontWeight;
  if (typeof source.italic === "boolean") style.italic = source.italic;
  if (typeof source.underline === "boolean") style.underline = source.underline;
  if (source.textAlign === "left" || source.textAlign === "center" || source.textAlign === "right") style.textAlign = source.textAlign;
  for (const key of ["textColor", "backgroundColor"] as const) {
    const color = source[key];
    if (typeof color === "string" && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) style[key] = color.toLowerCase();
  }
  for (const key of ["padding", "marginTop", "marginBottom"] as const) {
    const spacing = source[key];
    if (typeof spacing === "number" && spacing >= 0 && spacing <= 144) style[key] = spacing;
  }
  return Object.keys(style).length ? style : undefined;
};

export const writeStyle = (value: unknown): string | undefined => {
  const style = normalizeStyle(value);
  return style ? JSON.stringify(style) : undefined;
};

const mapBlockStyles = (blocks: unknown, convert: (style: unknown) => unknown): unknown => {
  if (!Array.isArray(blocks)) return blocks;
  // Drop holes and empty entries. Puck's next slot walk reads `.props` on every
  // child, and a saved sparse list crashes that walk.
  return blocks.flatMap((block) => {
    if (!block || typeof block !== "object" || Array.isArray(block)) return [];
    const typed = block as { props?: Record<string, unknown> };
    if (!typed.props || typeof typed.props !== "object") return [];
    const props = { ...typed.props };
    if ("style" in props) {
      const style = convert(props.style);
      if (style) props.style = style;
      else delete props.style;
    }
    if (Array.isArray(props.content)) props.content = mapBlockStyles(props.content, convert) as unknown[];
    return [{ ...block, props }];
  });
};

/** Puck edits style as one string. Saved template data keeps a style object. */
export const stylesForEditor = <T extends { content?: unknown; zones?: Record<string, unknown> }>(data: T): T => ({
  ...data,
  content: mapBlockStyles(data.content, writeStyle),
  zones: data.zones && Object.fromEntries(Object.entries(data.zones).map(([key, blocks]) => [key, mapBlockStyles(blocks, writeStyle)])),
});

export const stylesFromEditor = <T extends { content?: unknown; zones?: Record<string, unknown> }>(data: T): T => ({
  ...data,
  content: mapBlockStyles(data.content, normalizeStyle),
  zones: data.zones && Object.fromEntries(Object.entries(data.zones).map(([key, blocks]) => [key, mapBlockStyles(blocks, normalizeStyle)])),
});

// The editor writes a table as text: one row per line, cells separated by |, and \| for a literal |.
export const splitTableCells = (line: string): string[] => {
  const cells: string[] = [];
  let cell = "";
  for (let index = 0; index < line.length; index += 1) {
    if (line[index] === "\\" && line[index + 1] === "|") {
      cell += "|";
      index += 1;
    } else if (line[index] === "|") {
      cells.push(cell.trim());
      cell = "";
    } else cell += line[index];
  }
  cells.push(cell.trim());
  return cells;
};

const joinTableCells = (cells: unknown[]) => cells.map((cell) => String(cell ?? "").replace(/\|/g, "\\|")).join(" | ");

/** Accepts the editor text or the saved array, so normalizing twice keeps the same table. */
export const tableHeaders = (value: unknown): string[] => (Array.isArray(value)
  ? value.map((header) => String(header ?? "").trim())
  : splitTableCells(String(value ?? ""))).filter(Boolean);

export const tableRows = (value: unknown, columnCount: number): string[][] => {
  const rows = Array.isArray(value)
    ? value.map((row) => Array.isArray(row) ? row.map((cell) => String(cell ?? "").trim()) : splitTableCells(String(row ?? "")))
    : (() => {
      const text = String(value ?? "").replace(/(\r?\n)+$/, "");
      return text ? text.split(/\r?\n/).map(splitTableCells) : [];
    })();
  return rows.map((cells) => Array.from({length: columnCount}, (_, index) => cells[index] ?? ""));
};

const withoutPuckRuntimeMetadata = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(withoutPuckRuntimeMetadata);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value as Record<string, unknown>)
    .filter(([key]) => key !== "puck")
    .map(([key, child]) => [key, withoutPuckRuntimeMetadata(child)]));
};

const normalizeBlock = (value: unknown, definitions: InputDefinitions): PuckBlock | undefined => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const candidate = value as Partial<PuckBlock>;
  if (
    typeof candidate.type !== "string"
    || !validBlockTypes.has(candidate.type as keyof TemplateComponents)
  ) return undefined;
  // Puck may represent a component with no configured fields (notably Divider)
  // with null or an omitted props value.  Start from an empty object so strict
  // render-tree output always retains the required `{ type, props: {} }` shape.
  const hasObjectProps = candidate.props && typeof candidate.props === "object" && !Array.isArray(candidate.props);
  if (!hasObjectProps && candidate.type !== "Divider") return undefined;
  // Only Divider has no required fields, so Puck's null/omitted representation
  // is a valid empty Divider. Do not turn malformed props on other blocks (or
  // arrays/primitives on a Divider) into a valid persisted block.
  if (
    !hasObjectProps
    && candidate.props !== null
    && candidate.props !== undefined
  ) return undefined;
  const sourceProps = hasObjectProps ? candidate.props : {};
  const props = withoutPuckRuntimeMetadata(sourceProps) as Record<string, unknown>;
  if (Array.isArray(props.content)) {
    props.content = props.content
      .map((child) => normalizeBlock(child, definitions))
      .filter((child): child is PuckBlock => Boolean(child && (candidate.type !== "Page" || child.type !== "Page")));
  }
  const style = normalizeStyle(props.style);
  if (style) props.style = style;
  else delete props.style;
  if (candidate.type === "Paragraph") {
    const allowed = new Set(paragraphVariables(definitions).map((item) => item.variable));
    props.segments = normalizeParagraphSegments(props.segments, typeof props.text === "string" ? props.text : "")
      .map((segment) => {
        if (!("variable" in segment)) return segment;
        const variable = legacyVariableAliases[segment.variable] ?? segment.variable;
        return allowed.has(variable) ? { variable } : { text: segment.variable };
      });
    delete props.text;
  }
  if (candidate.type === "AutomaticValue" || candidate.type === "InlineAutomaticValue") {
    props.field = automaticField(props.field ?? props.key);
    delete props.key;
  }
  if (inputBlockTypes.has(candidate.type)) {
    const input = (parsePersistedObject(props.input) ?? { actor: props.actor, inputId: props.inputId }) as Partial<InputBinding>;
    const actor: InputActor | undefined = input?.actor === "employee" || input?.actor === "family" ? input.actor : undefined;
    const definition = actor ? findInputDefinition(definitions, actor, String(input?.inputId ?? "")) : undefined;
    if (!definition) return undefined;
    props.input = { actor, inputId: definition.id };
    delete props.actor;
    delete props.inputId;
  }
  if (candidate.type === "Table") {
    const headers = tableHeaders(props.headers);
    props.headers = headers.length ? headers : ["Column"];
    props.rows = tableRows(props.rows, (props.headers as string[]).length);
  }
  return { type: candidate.type as PuckBlock["type"], props };
};

/**
 * The API tree is deliberately not Puck's editing store.  PDF rendering only
 * accepts nested Page content and no Puck IDs/zones/runtime metadata.
 */
export const toPersistedRenderTree = (data: TemplateData, definitions: InputDefinitions): Record<string, unknown> => {
  // Children come from the editor data, not from the normalized page, so each child is normalized once.
  const zoneContent = (page: unknown) => {
    const props = (page as {props?: Record<string, unknown>}).props ?? {};
    const id = typeof props.id === "string" ? props.id : "";
    const zoned = id ? data.zones?.[`${id}:content`] : undefined;
    return Array.isArray(zoned) ? zoned : Array.isArray(props.content) ? props.content : [];
  };
  const persistBlock = (value: unknown, page = false): PuckBlock | undefined => {
    const normalized = normalizeBlock(value, definitions);
    if (!normalized) return undefined;
    const { id: _id, puck: _puck, content: _content, ...rest } = normalized.props;
    const props: Record<string, unknown> = {...rest};
    if (page) {
      const style = normalizeStyle(normalized.props.style);
      if (style) props.style = style;
      else delete props.style;
      props.content = zoneContent(value)
        .map((child) => persistBlock(child))
        .filter((child): child is PuckBlock => Boolean(child && child.type !== "Page"));
    }
    if (inputBlockTypes.has(normalized.type)) {
      const input = normalized.props.input as InputBinding;
      props.actor = input.actor;
      props.inputId = input.inputId;
      delete props.input;
    }
    const supportedProps: Record<keyof TemplateComponents, readonly string[]> = {
      Page: ["content", "style"],
      DocumentTitle: ["title", "style"],
      AutomaticValue: ["field", "style"],
      InlineAutomaticValue: ["field", "style"],
      SectionHeading: ["title", "style"],
      Paragraph: ["segments", "style"],
      Link: ["text", "url", "style"],
      TextInput: ["actor", "inputId", "style"],
      TextArea: ["actor", "inputId", "style"],
      DateInput: ["actor", "inputId", "style"],
      DateTimeInput: ["actor", "inputId", "style"],
      Checkbox: ["actor", "inputId", "style"],
      CheckboxGroup: ["actor", "inputId", "style"],
      RadioInput: ["actor", "inputId", "style"],
      Divider: ["style"],
      Table: ["headers", "rows", "style"],
    };
    return {
      type: normalized.type,
      props: Object.fromEntries(Object.entries(props).filter(([key]) => supportedProps[normalized.type].includes(key))),
    };
  };
  const pages = pageOnlyRoot((data.content as PuckBlock[])
    .map((block) => persistBlock(block, block.type === "Page"))
    .filter((block): block is PuckBlock => Boolean(block)));
  const rootProps = parsePersistedObject(data.root?.props);
  const title = typeof rootProps?.title === "string" ? rootProps.title : undefined;
  return {
    content: pages,
    ...(title ? {root: {props: {title}}} : {}),
  };
};

/** Convert strict backend data into the ID/slot-zone shape Puck 0.23 edits. */
export const toEditablePuckData = (tree: unknown, definitions: InputDefinitions): TemplateData => {
  const candidate = parsePersistedObject(tree) ?? {};
  // Puck keeps a module-level zone cache keyed by block id. Reopening a saved
  // template used to reuse those ids and restore the previous zone, including a
  // style object or a missing child from the last edit.
  const loadId = `b${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  const originalIds = new Map<string, string>();
  const usedBlockIds = new Set<string>();
  const uniqueBlockId = (suggested: string) => {
    const base = suggested || "puck-block";
    let id = base;
    for (let suffix = 2; usedBlockIds.has(id); suffix += 1) id = `${base}-${suffix}`;
    usedBlockIds.add(id);
    return id;
  };
  const legacyZones = parsePersistedObject(candidate.zones);
  const inflate = (value: unknown, path: string, parentType?: keyof TemplateComponents): PuckBlock | undefined => {
    const raw = parsePersistedObject(value);
    if (!raw || typeof raw.type !== "string" || !validBlockTypes.has(raw.type as keyof TemplateComponents)) return undefined;
    if (parentType === "Page" && raw.type === "Page") return undefined;
    const rawProps = parsePersistedObject(raw.props);
    if (!rawProps) return undefined;
    const props = withoutPuckRuntimeMetadata(rawProps) as Record<string, unknown>;
    const previousId = typeof props.id === "string" ? props.id : "";
    props.id = uniqueBlockId(`${loadId}-${path}-${raw.type.toLowerCase()}`);
    if (previousId) originalIds.set(String(props.id), previousId);
    const style = writeStyle(props.style);
    if (style) props.style = style;
    else delete props.style;
    if (inputBlockTypes.has(raw.type)) {
      const input = parsePersistedObject(props.input) ?? {actor: props.actor, inputId: props.inputId};
      if (input.actor === "employee" || input.actor === "family") props.input = {actor: input.actor, inputId: String(input.inputId ?? "")};
      delete props.actor;
      delete props.inputId;
    }
    if (raw.type === "AutomaticValue" || raw.type === "InlineAutomaticValue") {
      props.field = automaticField(props.field ?? props.key);
      delete props.key;
    }
    if (raw.type === "Table" && Array.isArray(props.headers)) {
      props.headers = joinTableCells(props.headers);
      props.rows = Array.isArray(props.rows) ? props.rows.map((row) => Array.isArray(row) ? joinTableCells(row) : "").join("\n") : "";
    }
    return {type: raw.type as PuckBlock["type"], props};
  };
  const rawContent = parsePersistedArray(candidate.content) ?? [];
  const pages = pageOnlyRoot(rawContent
    .map((block, index) => inflate(block, `content-${index}`))
    .filter((block): block is PuckBlock => Boolean(block)));
  const editablePages = pages.map((page, pageIndex) => {
    const pageId = String(page.props.id);
    const nested = parsePersistedArray(page.props.content);
    const legacy = parsePersistedArray(legacyZones?.[`${originalIds.get(pageId) ?? pageId}:content`]);
    // A strict Page's (including intentionally empty) nested content is
    // authoritative. Legacy zones are only a migration fallback when it is
    // absent, so stale runtime data cannot repopulate a cleared page.
    const children = (nested ?? legacy ?? [])
      .map((block, index) => inflate(block, `page-${pageIndex}-${index}`, "Page"))
      .filter((block): block is PuckBlock => Boolean(block));
    // Children must live in the Page's `content` slot, not in a
    // `${pageId}:content` zone. Puck 0.23 treats both as the same zone ID but
    // edits only the slot, so a zone copy leaves holes in the slot after the
    // first child edit and the next insert crashes reading `.props`.
    return {type: "Page" as const, props: {...page.props, content: children}};
  });
  const rawRoot = parsePersistedObject(candidate.root);
  const rootProps = parsePersistedObject(rawRoot?.props)
    ?? (rawRoot ? Object.fromEntries(Object.entries(rawRoot).filter(([key]) => key !== "readOnly" && key !== "puck")) : {});
  return {
    content: editablePages as TemplateData["content"],
    root: {props: withoutPuckRuntimeMetadata(rootProps) as Record<string, unknown>},
    zones: {},
  };
};

export const hydrateTemplate = (template?: Pick<ConsentTemplate, "structure" | "employeeInputs" | "familyMemberInputs">): {
  data: TemplateData;
  inputDefinitions: InputDefinitions;
} => {
  const cloneDefinitions = (inputs: ConsentTemplateInput[] | undefined) => inputs?.map((input) => {
    const type = input.type as string;
    return {
      ...input,
      type: (type === "textarea" ? "text" : type === "choice" ? "checkbox_group" : input.type) as ConsentTemplateInput["type"],
      ...(type === "textarea" ? {multiline: true} : {}),
      options: input.options?.map((option) => ({ ...option })),
    };
  }) ?? [];
  const inputDefinitions = {
    employee: cloneDefinitions(template?.employeeInputs),
    family: cloneDefinitions(template?.familyMemberInputs),
  };
  const structure = parsePersistedObject(template?.structure);
  const editor = parsePersistedObject(structure?.editor);
  return {data: toEditablePuckData(editor?.puck, inputDefinitions), inputDefinitions};
};

/** Same rule as the PDF renderer on the server: https only, without credentials. */
export const isAllowedLinkUrl = (value: unknown): boolean => {
  if (typeof value !== "string" || !value.trim() || value.length > 2048) return false;
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" && !url.username && !url.password;
  } catch {
    return false;
  }
};

export const validateTemplate = (data: TemplateData, definitions: InputDefinitions): string[] => {
  const issues: string[] = [];
  const ids = new Set<string>();
  (["employee", "family"] as const).forEach((actor) => definitions[actor].forEach((input) => {
    const reference = `${actor}.${input.id || "(missing ID)"}`;
    if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(input.id) || input.id.length > 100) issues.push(`${reference}: invalid input ID.`);
    if (!input.label.trim() || input.label.length > 500) issues.push(`${reference}: invalid input label.`);
    if (ids.has(input.id)) issues.push(`${reference}: input ID is already used.`);
    if ((automaticKeyValues as readonly string[]).includes(`${actor}.${input.id}`)) issues.push(`${reference}: input ID is reserved for an automatic value. Rename the input.`);
    ids.add(input.id);
    if ((input.type === "radio" || input.type === "checkbox_group") && !input.options?.length) issues.push(`${reference}: add at least one option.`);
    if (input.type !== "radio" && input.type !== "checkbox_group" && input.options?.length) issues.push(`${reference}: only radio and checkbox groups may have options.`);
    const optionIds = new Set<string>();
    input.options?.forEach((option) => {
      if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(option.id) || option.id.length > 100 || !option.label.trim() || option.label.length > 500) {
        issues.push(`${reference}: invalid option ID or label.`);
      }
      if (optionIds.has(option.id)) issues.push(`${reference}: option ID "${option.id}" is repeated.`);
      optionIds.add(option.id);
    });
  }));
  const visit = (block: PuckBlock) => {
    if (inputBlockTypes.has(block.type)) {
      const input = block.props.input as Partial<InputBinding> | undefined;
      const definition = input && (input.actor === "employee" || input.actor === "family")
        ? findInputDefinition(definitions, input.actor, String(input.inputId ?? ""))
        : undefined;
      const hasExpectedType = definition && (
        (block.type === "TextInput" && definition.type === "text" && !definition.multiline)
        || (block.type === "TextArea" && definition.type === "text" && definition.multiline)
        || (block.type === "DateInput" && definition.type === "date")
        || (block.type === "DateTimeInput" && definition.type === "datetime")
        || (block.type === "Checkbox" && definition.type === "checkbox")
        || (block.type === "CheckboxGroup" && definition.type === "checkbox_group")
        || (block.type === "RadioInput" && definition.type === "radio")
      );
      if (!hasExpectedType) {
        issues.push(`${block.type}: missing input definition.`);
      }
    }
    if (block.type === "Link") {
      const text = typeof block.props.text === "string" ? block.props.text.trim() : "";
      const label = text ? `Link "${text}"` : "Link";
      if (!text || text.length > 500) issues.push(`${label}: link text is required (up to 500 characters).`);
      if (!isAllowedLinkUrl(block.props.url)) issues.push(`${label}: use a full https:// address, for example https://www.health.gov.au/.`);
    }
    if (Array.isArray(block.props.content)) block.props.content.forEach((child) => visit(child as PuckBlock));
  };
  (data.content as PuckBlock[]).forEach((page) => {
    visit(page);
    const id = typeof page.props.id === "string" ? page.props.id : "";
    data.zones?.[`${id}:content`]?.forEach(visit);
  });
  return issues;
};

const escapeHtml = (value: unknown) => String(value ?? "").replace(/[&<>"']/g, (character) =>
  ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[character] ?? character))
  .replace(/{{/g, "{&#123;").replace(/}}/g, "&#125;}");

export const renderTemplateHtml = (value: unknown) =>
  parseTemplateText(value).map((part) => "text" in part ? escapeHtml(part.text) : `{{${part.variable}}}`).join("");

/** Portable document defaults for preview, mobile, and PDF. Block look comes from Style configuration. */
export const templateDocumentCss = "@page { size: A4; margin: 18mm; } body { color: #111827; font-family: Helvetica, Arial, sans-serif; font-size: 10pt; } .consent-page { page-break-after: always; } p { line-height: 1.4; } hr { border: 0; border-top: 1px solid #64748b; margin: 16px 0; } table { border-collapse: collapse; table-layout: fixed; width: 100%; } th, td { border: 1px solid #64748b; padding: 6px; text-align: left; vertical-align: top; } th { background: #e2e8f0; }";

export const buildTemplatePayload = (data: TemplateData, definitions: InputDefinitions): TemplatePayload => {
  const puck = toPersistedRenderTree(data, definitions);
  const persistedContent = puck.content as PuckBlock[];
  const classes = new Map<string, string>();
  const classFor = (style: BlockStyle | undefined) => {
    if (!style) return "";
    const key = JSON.stringify(style);
    if (!classes.has(key)) classes.set(key, `puck-style-${classes.size + 1}`);
    return classes.get(key) ?? "";
  };
  const render = (block: PuckBlock): string => {
    const style = block.props.style as BlockStyle | undefined;
    const className = classFor(style);
    const attr = className ? ` class="${className}"` : "";
    const children = Array.isArray(block.props.content) ? block.props.content as PuckBlock[] : [];
    switch (block.type) {
      case "Page": return `<article class="${["consent-page", className].filter(Boolean).join(" ")}">${children.map(render).join("")}</article>`;
      case "DocumentTitle": return `<h1${attr}>${renderTemplateHtml(block.props.title)}</h1>`;
      case "SectionHeading": return `<h2${attr}>${renderTemplateHtml(block.props.title)}</h2>`;
      case "Paragraph": return `<p${attr}>${renderTemplateHtml(block.props.segments)}</p>`;
      case "Link": return isAllowedLinkUrl(block.props.url)
        ? `<a${attr} href="${escapeHtml(String(block.props.url).trim())}">${escapeHtml(block.props.text)}</a>`
        : `<span${attr}>${escapeHtml(block.props.text)}</span>`;
      case "AutomaticValue": return `<p${attr}>{{${block.props.field}}}</p>`;
      case "InlineAutomaticValue": return `<span${attr}>{{${block.props.field}}}</span>`;
      case "Divider": return `<hr${attr}>`;
      case "Table": {
        const headers = block.props.headers as string[]; const rows = block.props.rows as string[][];
        return `<table${attr}><thead><tr>${headers.map((header) => `<th>${renderTemplateHtml(header)}</th>`).join("")}</tr></thead><tbody>${rows.map((row) => `<tr>${headers.map((_, index) => `<td>${renderTemplateHtml(row[index])}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
      }
      case "TextInput":
      case "TextArea":
      case "DateInput":
      case "DateTimeInput":
      case "Checkbox":
      case "CheckboxGroup":
      case "RadioInput": {
        const actor = block.props.actor;
        const inputId = String(block.props.inputId ?? "");
        const definition = actor === "employee" || actor === "family"
          ? findInputDefinition(definitions, actor, inputId)
          : undefined;
        return `<section${attr}><p><strong>${renderTemplateHtml(definition?.label)}</strong></p><p>{{${actor ?? ""}.${inputId}}}</p></section>`;
      }
      default: return "";
    }
  };
  const html = persistedContent.map(render).join("");
  const declarations = Array.from(classes.entries()).map(([serialized, name]) => {
    const style = JSON.parse(serialized) as BlockStyle;
    return `.${name}{${style.fontFamily ? `font-family:${style.fontFamily};` : ""}${style.fontSize ? `font-size:${style.fontSize}pt;` : ""}${style.fontWeight ? `font-weight:${style.fontWeight};` : ""}${style.italic ? "font-style:italic;" : ""}${style.underline ? "text-decoration:underline;" : ""}${style.textAlign ? `text-align:${style.textAlign};` : ""}${style.textColor ? `color:${style.textColor};` : ""}${style.backgroundColor ? `background-color:${style.backgroundColor};` : ""}${style.padding !== undefined ? `padding:${style.padding}px;` : ""}${style.marginTop !== undefined ? `margin-top:${style.marginTop}px;` : ""}${style.marginBottom !== undefined ? `margin-bottom:${style.marginBottom}px;` : ""}}`;
  }).join("");
  return {
    structure: { html, css: `${templateDocumentCss}${declarations}`, editor: { puck } },
    employeeInputs: sortConsentInputsByDocument(toInputDtos(definitions.employee), html, "employee"),
    familyMemberInputs: sortConsentInputsByDocument(toInputDtos(definitions.family), html, "family"),
  };
};

const toInputDtos = (inputs: ConsentTemplateInput[]): ConsentTemplateInput[] => inputs.map((input) => {
  const internalType = input.type as string;
  const type = internalType === "textarea" ? "text"
    : internalType === "choice" ? "checkbox_group"
      : input.type;
  const base = {id: input.id, label: input.label, type: type as ConsentTemplateInput["type"]};
  if (type === "text") return {...base, ...(input.multiline || internalType === "textarea" ? {multiline: true} : {})};
  if (type === "checkbox" || type === "date" || type === "datetime") return base;
  return {...base, options: (input.options ?? []).map(({id, label}) => ({id, label}))};
});
